import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { STORE_PERMISSIONS } from "@/lib/permissions";

export interface Store {
  _id: string;
  storeName: string;
  businessName: string;
  businessType: string;
  whatsappNumber: string;
  currency: string;
  logo: string;
  url: string;
  address?: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  socialLinks?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    tiktok?: string;
    youtube?: string;
    website?: string;
  };
  businessHours?: Array<{
    day: string;
    open: boolean;
    openTime: string;
    closeTime: string;
  }>;
  businessDescription?: string;
  customization?: {
    theme?: string;
    colors?: {
      primary: string;
      secondary: string;
      accent: string;
    };
    layout?: string;
  };
  createdAt: string;
  owner: string;
}

export interface CreateStoreData {
  storeName: string;
  businessName: string;
  businessType: string;
  whatsappNumber: string;
  currency: string;
  url: string;
  logo?: string; // Base64 encoded logo image
}

export interface StoreContextType {
  createStore: (data: CreateStoreData) => Promise<Store>;
  isLoading: boolean;
  isCreating: boolean;
  hasPermission: (permission: string) => boolean;
  availablePermissions: string[];
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

// List of all possible permissions
const AVAILABLE_PERMISSIONS = STORE_PERMISSIONS.map((permission) => permission.id);

export const StoreProvider = ({ children }: { children: ReactNode }) => {
  const { user, currentStore, fetchUserStores } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Function to check if user has permission for a specific feature
  const hasPermission = (permission: string): boolean => {
    if (!currentStore) {
      return false;
    }

    // Store owners have all permissions
    if (currentStore.isOwner === true) {
      return true;
    }

    // Check if the manager has the specific permission
    const hasAccess =
      Array.isArray(currentStore.permissions) &&
      currentStore.permissions.includes(permission);
    return hasAccess;
  };

  const createStore = async (data: CreateStoreData): Promise<Store> => {
    setIsCreating(true);
    try {
      // Check logo size if present
      if (data.logo) {
        // Estimate the size of the base64 data in bytes
        const logoSizeKB = Math.ceil(((data.logo.length - 22) * 0.75) / 1024);

        // Warn if logo is large (but still attempt to upload)
        if (logoSizeKB > 500) {
          console.warn("Logo is quite large, which might cause upload issues");
        }
      }

      const response = await api.post("/api/stores", data);
      toast.success("Store created successfully");

      // After creating store, refresh the user's stores list
      await fetchUserStores();

      return response.data;
    } catch (error: any) {
      console.error("Store creation error:", error);

      // Handle specific error types
      if (error.response) {
        if (error.response.status === 413) {
          toast.error("Logo image is too large. Please use a smaller image.");
        } else {
          const errorMessage =
            error.response.data?.message || "Failed to create store";
          toast.error(errorMessage);
        }
      } else {
        toast.error("Network error. Please try again later.");
      }

      throw error;
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <StoreContext.Provider
      value={{
        createStore,
        isLoading,
        isCreating,
        hasPermission,
        availablePermissions: AVAILABLE_PERMISSIONS,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error("useStore must be used within a StoreProvider");
  }
  return context;
};
